import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, TrendingUp, Globe, Eye, Sparkles } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { fetchBrandRadar } from "@/lib/api";

const SOURCES = [
  { key: 'chatgpt', label: 'ChatGPT', color: '#10B981' },
  { key: 'perplexity', label: 'Perplexity', color: '#8B5CF6' },
  { key: 'gemini', label: 'Gemini', color: '#3B82F6' },
  { key: 'google_ai_overviews', label: 'Google AI', color: '#F59E0B' },
  { key: 'copilot', label: 'Copilot', color: '#06B6D4' },
];

const BRAND_COLORS = {
  'jbhifi.com.au': '#FFEE2F', 'thegoodguys.com.au': '#3B82F6',
  'harveynorman.com.au': '#EF4444', 'officeworks.com.au': '#10B981',
  'amazon.com.au': '#FF9900',
};

const BRAND_NAMES = {
  'jbhifi.com.au': 'JB Hi-Fi', 'thegoodguys.com.au': 'The Good Guys',
  'harveynorman.com.au': 'Harvey Norman', 'officeworks.com.au': 'Officeworks',
  'amazon.com.au': 'Amazon',
};

export default function AIVisibilityPage() {
  const [brands, setBrands] = useState([]);
  const [activeSource, setActiveSource] = useState('chatgpt');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchBrandRadar();
        setBrands(data.brands || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const jb = brands.find(b => b.domain === 'jbhifi.com.au');
  const sourceColor = SOURCES.find(s => s.key === activeSource)?.color || '#10B981';

  // Build SOV comparison chart data
  const sovChartData = (() => {
    if (!brands.length) return [];
    const dateMap = {};
    for (const brand of brands) {
      const history = brand.impressions_history?.[activeSource] || [];
      for (const h of history) {
        if (!dateMap[h.date]) dateMap[h.date] = { date: h.date };
        dateMap[h.date][brand.domain] = h.sov;
      }
    }
    return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
  })();

  // Build impressions chart data for JB Hi-Fi
  const impressionsData = (() => {
    if (!jb) return [];
    const history = jb.impressions?.[activeSource] || [];
    return history.map(h => ({ date: h.date, impressions: h.impressions }));
  })();

  if (loading) {
    return <div className="space-y-6" data-testid="ai-visibility-loading">
      {[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-gray-100 animate-pulse rounded-none" />)}
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="ai-visibility-page">
      {/* SOV Score Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="sov-cards">
        {brands.map(brand => {
          const color = BRAND_COLORS[brand.domain] || '#6B7280';
          const avgSov = (() => {
            const vals = Object.values(brand.sov || {}).filter(v => v > 0);
            return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : '0';
          })();
          return (
            <Card key={brand.domain} className="glass-card border-gray-200 rounded-none" data-testid={`sov-card-${brand.domain}`}>
              <CardContent className="py-4 px-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
                  <span className="font-display text-[10px] text-gray-500 uppercase tracking-widest">{brand.name}</span>
                </div>
                <p className="font-mono-data text-2xl font-bold" style={{ color }}>{avgSov}%</p>
                <p className="font-mono-data text-[10px] text-gray-400 mt-1">AI Share of Voice</p>
                <div className="flex gap-2 mt-2">
                  {SOURCES.map(src => (
                    <span key={src.key} className="font-mono-data text-[9px] px-1.5 py-0.5" style={{ color: src.color, background: `${src.color}12`, border: `1px solid ${src.color}25` }}>
                      {src.label}: {brand.sov?.[src.key] ?? 0}%
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Source Selector */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        {SOURCES.map(src => (
          <button
            key={src.key}
            onClick={() => setActiveSource(src.key)}
            className={`px-6 py-3 font-display text-sm font-bold uppercase tracking-wider transition-all duration-200 border-b-2 ${
              activeSource === src.key ? 'text-white border-current' : 'text-gray-400 border-transparent hover:text-gray-600'
            }`}
            style={activeSource === src.key ? { color: src.color, borderColor: src.color } : {}}
            data-testid={`source-tab-${src.key}`}
          >
            {src.label}
          </button>
        ))}
      </div>

      {/* SOV Trend Chart */}
      <Card className="glass-card border-gray-200 rounded-none" data-testid="sov-chart">
        <CardHeader className="pb-2 border-b border-gray-200">
          <CardTitle className="font-display text-sm font-semibold tracking-wider uppercase text-gray-800 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" style={{ color: sourceColor }} />
            Share of Voice Trend — {SOURCES.find(s => s.key === activeSource)?.label}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {sovChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={sovChartData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fill: '#6B7280', fontSize: 10, fontFamily: 'Inter, Arial, sans-serif' }}
                  tickFormatter={d => { const dt = new Date(d); return `${dt.getDate()}/${dt.getMonth()+1}`; }} />
                <YAxis tick={{ fill: '#6B7280', fontSize: 10 }} unit="%" />
                <Tooltip
                  contentStyle={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 0, fontFamily: 'Inter, Arial, sans-serif', fontSize: 11 }}
                  formatter={(v) => [`${v}%`, '']}
                />
                <Legend wrapperStyle={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 11, paddingTop: 10 }} />
                {brands.map(brand => (
                  <Line key={brand.domain} type="monotone" dataKey={brand.domain} name={BRAND_NAMES[brand.domain]}
                    stroke={BRAND_COLORS[brand.domain]} strokeWidth={brand.domain === 'jbhifi.com.au' ? 3 : 1.5}
                    dot={false} opacity={brand.domain === 'jbhifi.com.au' ? 1 : 0.6} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400 font-mono-data text-sm">
              <Sparkles className="w-5 h-5 mr-2 text-gray-400" /> Data loading... Brand Radar fetches every 24 hours.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Impressions Chart (JB Hi-Fi) */}
      <Card className="glass-card border-gray-200 rounded-none" data-testid="impressions-chart">
        <CardHeader className="pb-2 border-b border-gray-200">
          <CardTitle className="font-display text-sm font-semibold tracking-wider uppercase text-gray-800 flex items-center gap-2">
            <Eye className="w-4 h-4 text-[#FFEE2F]" />
            JB Hi-Fi AI Impressions — {SOURCES.find(s => s.key === activeSource)?.label}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {impressionsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={impressionsData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fill: '#6B7280', fontSize: 10, fontFamily: 'Inter, Arial, sans-serif' }}
                  tickFormatter={d => { const dt = new Date(d); return `${dt.getDate()}/${dt.getMonth()+1}`; }} />
                <YAxis tick={{ fill: '#6B7280', fontSize: 10 }} />
                <Tooltip contentStyle={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 0, fontFamily: 'Inter, Arial, sans-serif', fontSize: 11 }} />
                <Line type="monotone" dataKey="impressions" stroke="#FFEE2F" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400 font-mono-data text-sm">No impressions data yet</div>
          )}
        </CardContent>
      </Card>

      {/* Top Cited Pages */}
      <Card className="glass-card border-gray-200 rounded-none" data-testid="cited-pages">
        <CardHeader className="pb-2 border-b border-gray-200">
          <CardTitle className="font-display text-sm font-semibold tracking-wider uppercase text-gray-800 flex items-center gap-2">
            <Globe className="w-4 h-4 text-gray-500" />
            Top Cited Pages (ChatGPT)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {brands.filter(b => (b.cited_pages || []).length > 0).length === 0 ? (
            <div className="py-12 text-center font-mono-data text-sm text-gray-400">No cited pages data yet</div>
          ) : (
            brands.map(brand => {
              const pages = brand.cited_pages || [];
              if (!pages.length) return null;
              const color = BRAND_COLORS[brand.domain];
              return (
                <div key={brand.domain} className="mb-4 last:mb-0">
                  <div className="flex items-center gap-2 py-2 border-b border-gray-200">
                    <span className="w-2 h-2 rounded-sm" style={{ background: color }} />
                    <span className="font-display text-[10px] font-semibold uppercase tracking-widest" style={{ color }}>{brand.name}</span>
                    <Badge className="rounded-sm text-[9px] font-mono-data border-none px-1.5 py-0 bg-gray-100 text-gray-500">{pages.length} pages</Badge>
                  </div>
                  {pages.slice(0, 10).map((p, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 hover:bg-gray-50 transition-colors" data-testid={`cited-page-${brand.domain}-${i}`}>
                      <a href={`https://${p.url}`} target="_blank" rel="noopener noreferrer" className="font-mono-data text-xs text-gray-600 hover:text-white transition-colors flex items-center gap-1.5 truncate max-w-[70%]">
                        {p.url}
                        <ExternalLink className="w-3 h-3 flex-shrink-0 text-gray-400" />
                      </a>
                      <span className="font-mono-data text-xs font-bold" style={{ color }}>{p.responses} responses</span>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}
